/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// utils
// material
import {
    ListTable,
    Group,
    Button,
    Title,
    Icon,
    ToolTip,
    Loader,
    InlineMessage,
    displayModal,
    log,
    FileUploader
} from '@insticator/insticator-ui'; // iui-material
import ConfigController from "../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import SectionCard from "../../../../material/expandablesection/sectioncard/sectioncard";

// styles
import injectSheet from 'react-jss';
import styles from './adServerBlockingInnards_styles';
import {getBlockListCSV, uploadBlockListCSV} from "../../../../state/thunks/adServeBlocking";


const defaultCols = {
    'Site or app': 'Site or app',
    'Entity': 'Entity',
    'Platform': 'Platform',
    'Issue location': 'Issue location',
    'Must fix': 'Must fix',
    'Status': 'Status'
}

const statusList = ['Pending', 'Prepared', 'Skipped', 'Empty', 'In Process', 'Postponed', 'Retrying', 'Failed', 'Ported']
const csvFileTypes = ['text/csv', 'application/csv', 'text/comma-separated-values', 'application/vnd.ms-excel', 'application/vnd.msexcel', 'application/excel', 'text/anytext']

/* ========== ~~~~~~~~~~ IMPORT COMMENTS ~~~~~~~~~~ ========== */
const AdServeBlockingInnards = injectSheet(styles)((props) => {

    const dispatch = useDispatch();

    const [isOpened, setIsOpened] = useState(true);
    const [isNewUploadFormOpen, openNewUploadForm] = useState(false);
    const [isLoading, loading] = useState(false)
    const [isRefreshing, refresh] = useState(false)
    const [file, setFile] = useState(null)

    const blockListData = useSelector(state => state.settings.blockList)

    useEffect(() => {
        return () => {
            openNewUploadForm(false)
        }
    },[])

    useEffect(() => {
        dispatch(getBlockListCSV())
    }, [])


    const handleToggle = () => {
        setIsOpened(!isOpened)
        openNewUploadForm(false)
    }

    const handleSaveAndClose = () => {
        openNewUploadForm(false)
        if (!file) return
        dispatch(uploadBlockListCSV({file}))
    }

    const controller = (
        <ConfigController
            handleToggle={handleToggle}
            handleCancel={handleToggle}
            isOpened={isOpened}
            configTitle={'View'}
            cancelTitle={'Close'}
            title='Upload Comments'
        />
    );

    const formatDataStatus = type => {
        return (
            <ul>
                {statusList.map(status => <li><span>{status}: </span><span>{ type[status.toLowerCase().replace(/\s/g, '')] }</span></li>)}
                <li><span>Total:</span> <span>{statusList.map(status => type[status.toLowerCase().replace(/\s/g, '')]).reduce((total, num) => total + num, 0)}</span></li>
            </ul>
        )
    }

    const formatDateToLocalTime = (timestamp) => {
        var localDate = new Date(timestamp);
        if(localDate.getTime() < 0) return ''; // return blank if date is invalid

        const year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(localDate);
        const month = new Intl.DateTimeFormat("en", { month: "short" }).format(localDate);
        const day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(localDate);
        const time = new Intl.DateTimeFormat("default", {
            hour: "numeric",
            minute: "numeric",
            second: "numeric"
        }).format(localDate);

        return `${month} ${day}, ${year} ${time}`;
    };

    const formattedData = () => (blockListData || []).map(data => {
        return data
    })

    const showStatusLogsModal = logs => dispatch(displayModal(true,
        <div className={'logsmodal'}>
            <h4>Upload Logs</h4>
            <ul>
                {logs.map(log => <li>{log}</li>)}
            </ul>
        </div>)
    )

    const handlePreview = id => window.open(`/dashboard/commenting/import/preview/${id}`)

    const handleStartUpload = () => {
        openNewUploadForm(false)
        handleRefresh()
    }

    const handleLoad = useCallback(async() => {

    }, [dispatch])

    const handleRefresh = useCallback(async() => {
    }, [dispatch])

    const handleCancel = () => dispatch()

    const isUploadRunning = false

    const newUploadBtn = <Button text={'Upload New CSV'} variant={'primary'} handleClick={() => openNewUploadForm(true)} disabled={false} />

    return (
        <div className={`csvUploader ${props.classes.CSVUploader}`}>
            {isOpened && <>
                <SectionCard>
                    <Group name={'imports-header'}>
                        <Title><h2>Blocked URLs</h2></Title>
                        <Group name={'imports-table-action'}>
                            { !isNewUploadFormOpen &&
                                <>
                                    { isUploadRunning ?
                                        <ToolTip
                                            trigger={'mouseenter focus'}
                                            content={<div><Icon name={'exclamation-circle'} /> Only 1 import can be run at a time.</div>}
                                        >
                                            { newUploadBtn }
                                        </ToolTip>
                                        :
                                        newUploadBtn
                                    }
                                </>
                            }
                        </Group>
                    </Group>
                    { isNewUploadFormOpen &&
                        <Group>
                            <Group name={'imports-header'}>
                                <Title><h2>New Import</h2></Title>
                            </Group>
                            <Group name={'file-uploader'}>
                                <FileUploader
                                    uploadFile={file => setFile(file)}
                                    discardFile={() => setFile(null)}
                                    supportedFileTypes={csvFileTypes}
                                    disabled={false}
                                />
                            </Group>
                            <Group name={'action-buttons'}>
                                <Button text={'Cancel'} variant={'neutral'} handleClick={() => openNewUploadForm(false)} disabled={false} />
                                <Button text={'Save & Close'} variant={'primary'} handleClick={() => handleSaveAndClose()} disabled={false} />
                            </Group>
                        </Group>
                    }
                    { isRefreshing || isLoading ?
                        <div className={'loaderwrapper'}><Loader /></div>
                        :
                        <>
                            <ListTable
                                name={`imports`}
                                size={`full`}
                                columnHeads={defaultCols}
                                data={formattedData()}
                                rowsPerPage={10}
                                paginationLocation={`bottom`}
                            />
                        </>
                    }
                </SectionCard>
            </>}
        </div>
    )
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default AdServeBlockingInnards